import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import StopIcon from "../../../../../../assets/images/icons/StopIcon";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import { useAppStore } from "../../../../../../hooks/useStores";
import { isPlaygroundLoadingImages } from "../../../../../../utils";
import styles from "../TextToImageSearch.module.scss";

type Props = {
	handleGenerate?: () => Promise<void>;
	onError?: () => void;
	onStop?: () => void;
	className?: string;
	isTgEvaluation?: boolean;
};
const TextToImageGenerateButton = ({ handleGenerate, onError, onStop, className, isTgEvaluation }: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.generator.searchBox" });
	const { textToImageStore, authStore, playgroundStore, fiboStore } = useAppStore();
	const hasLoadingImages = isPlaygroundLoadingImages(
		playgroundStore.playgroundResults,
		playgroundStore.selectedConfig,
	);

	const isProOrFibo = ["fibo"].includes(textToImageStore.config.model);

	const hasPrompt = textToImageStore.config.prompt || textToImageStore.config.generation_prefix;
	const hasImage = textToImageStore.imageReference.file;
	const canGenerateContent = isProOrFibo ? hasPrompt || hasImage : hasPrompt;

	const isGenerateEnabled =
		(textToImageStore.config.model || textToImageStore.config.tailored_model_id) &&
		canGenerateContent &&
		!authStore.isLoadingOrgSubscriptions &&
		!authStore.orgPassedFreeLimit &&
		!authStore.isSubscriptionInactive &&
		!hasLoadingImages &&
		!textToImageStore.controlNetAspectRatioError;

	const isFiboTgModel =
		!!textToImageStore.config.tailored_model_id && textToImageStore.config.training_version === "fibo";
	const isPromptEmpty = !textToImageStore.config.prompt || textToImageStore.config.prompt.trim().length === 0;
	const shouldDisableForEmptyPrompt = isFiboTgModel && isPromptEmpty;

	const handleClick = async () => {
		textToImageStore.config.is_refine = false;
		try {
			if (handleGenerate) {
				await handleGenerate();
			} else {
				await textToImageStore.generateTextToImage(undefined, isTgEvaluation);
			}
		} catch (err: any) {
			onError?.();
		}
	};

	const handleAbort = () => {
		textToImageStore.config.is_refine = false;
		onStop?.();

		if (textToImageStore.config.model === "fibo") {
			fiboStore.abortImageGeneration();
		} else {
			textToImageStore.abortImageGeneration();
		}
	};

	return (
		<>
			{hasLoadingImages ? (
				<BriaButton
					buttonType="primaryMedium"
					className={clsx(className, styles.button, styles.stopGeneratingBtn)}
					onClick={handleAbort}
				>
					<StopIcon /> {t("stop")}
				</BriaButton>
			) : (
				<BriaButton
					buttonType="primaryMedium"
					className={`${className || ""} ${styles.button}`}
					onClick={handleClick}
					loading={authStore.isLoadingOrgSubscriptions}
					disabled={
						!isGenerateEnabled || textToImageStore.isUploadingImageReference || shouldDisableForEmptyPrompt
					}
				>
					{t("button")}
				</BriaButton>
			)}
		</>
	);
};

export default observer(TextToImageGenerateButton);
